/**
 * Plan 25 — Kiosk session client API.
 *
 * TanStack Query hooks over `/api/kiosk-sessions/*` (Plan 22's REST
 * surface). The Devices settings page consumes these to list paired
 * kiosks, pair a new one, and revoke + clear-soft-warn.
 *
 * Surface mirrors `apps/ordo/server/src/routes/kiosk-sessions.ts`
 * exactly — wire shapes are camelCase strings/ISO dates, the same
 * shape the server returns.
 *
 * Per the apps/ordo §"API style" policy: REST for the customer
 * client, GraphQL for the AI tool. This file is REST-only.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

import { apiFetch } from './http'

// ---------------------------------------------------------------------------
// Wire shapes — match `serializeSession` on the server.
// ---------------------------------------------------------------------------

export interface KioskSessionWire {
  id: string
  tokenHashPrefix: string
  locationId: string
  pairedByUserId: string
  browserIdentifier: string
  pairedAtIp: string
  hardwareHint: string | null
  pairedAt: string
  lastHeartbeatAt: string | null
  lastVerifiedAt: string | null
  softWarnAt: string | null
  revokedAt: string | null
  revokedByUserId: string | null
  isArchived: boolean
}

export interface ListKioskSessionsResponse {
  sessions: KioskSessionWire[]
}

export interface KioskSessionDetailResponse {
  session: KioskSessionWire
}

export interface PairKioskDevicePayload {
  locationId: string
  browserIdentifier: string
  userAgent?: string
  hardwareHint?: string | null
}

export interface PairKioskDeviceResponse {
  session: KioskSessionWire
  /**
   * Plaintext bearer token — returned ONCE on the pair response.
   * Persist client-side (this is what gets stored on the tablet via
   * `localStorage.kioskToken`); the server never exposes it again
   * after this response.
   */
  token: string
}

// ---------------------------------------------------------------------------
// Fetch helpers
// ---------------------------------------------------------------------------

export async function fetchKioskSessionsForLocation(
  locationId: string,
  signal?: AbortSignal,
): Promise<ListKioskSessionsResponse> {
  return apiFetch<ListKioskSessionsResponse>('/api/kiosk-sessions', {
    query: { locationId },
    signal,
  })
}

export async function pairKioskDevice(payload: PairKioskDevicePayload): Promise<PairKioskDeviceResponse> {
  return apiFetch<PairKioskDeviceResponse>('/api/kiosk-sessions/pair', {
    method: 'POST',
    body: payload,
  })
}

export async function revokeKioskSession(sessionId: string): Promise<KioskSessionDetailResponse> {
  return apiFetch<KioskSessionDetailResponse>(`/api/kiosk-sessions/${sessionId}/revoke`, {
    method: 'POST',
    body: {},
  })
}

export async function clearKioskSoftWarn(sessionId: string): Promise<KioskSessionDetailResponse> {
  return apiFetch<KioskSessionDetailResponse>(`/api/kiosk-sessions/${sessionId}/clear-soft-warn`, {
    method: 'POST',
    body: {},
  })
}

// ---------------------------------------------------------------------------
// Force-update (Plan 23 sender half)
// ---------------------------------------------------------------------------

export interface ForceUpdateResponse {
  requestedAt: string
}

export interface ForceUpdateAllResponse {
  requestedSessionIds: string[]
}

export interface ClaimedKioskCommandsResponse {
  forceUpdate: { requestedAt: string } | null
}

/** Queue a FORCE_UPDATE the kiosk drains on its next command poll. */
export async function forceUpdateKioskSession(sessionId: string): Promise<ForceUpdateResponse> {
  return apiFetch<ForceUpdateResponse>(`/api/kiosk-sessions/${sessionId}/force-update`, {
    method: 'POST',
    body: {},
  })
}

/** Bulk form — every active kiosk at the location. */
export async function forceUpdateAllKioskSessions(locationId: string): Promise<ForceUpdateAllResponse> {
  return apiFetch<ForceUpdateAllResponse>('/api/kiosk-sessions/force-update-all', {
    method: 'POST',
    body: { locationId },
  })
}

/** Kiosk-side: drain this session's pending commands (kiosk bearer only). */
export async function claimKioskCommands(sessionId: string): Promise<ClaimedKioskCommandsResponse> {
  return apiFetch<ClaimedKioskCommandsResponse>(`/api/kiosk-sessions/${sessionId}/commands/claim`, {
    method: 'POST',
    body: {},
  })
}

// ---------------------------------------------------------------------------
// Query keys
// ---------------------------------------------------------------------------

export const kioskSessionsForLocationKey = (locationId: string) => ['kiosk-sessions', { locationId }] as const

// ---------------------------------------------------------------------------
// Hooks
// ---------------------------------------------------------------------------

export function useKioskSessionsForLocationQuery(locationId: string | null) {
  return useQuery<ListKioskSessionsResponse>({
    enabled: Boolean(locationId),
    queryKey: kioskSessionsForLocationKey(locationId ?? ''),
    queryFn: ({ signal }) => fetchKioskSessionsForLocation(locationId ?? '', signal),
    staleTime: 30 * 1000,
    // Refresh every 60s so the heartbeat badge in the admin list
    // turns red without a manual reload when a kiosk goes offline.
    refetchInterval: 60 * 1000,
  })
}

export function usePairKioskDeviceMutation(locationId: string | null) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: pairKioskDevice,
    onSuccess: () => {
      if (locationId) {
        void qc.invalidateQueries({ queryKey: kioskSessionsForLocationKey(locationId) })
      }
    },
  })
}

export function useRevokeKioskSessionMutation(locationId: string | null) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: revokeKioskSession,
    onSuccess: () => {
      if (locationId) {
        void qc.invalidateQueries({ queryKey: kioskSessionsForLocationKey(locationId) })
      }
    },
  })
}

export function useForceUpdateKioskSessionMutation() {
  return useMutation({ mutationFn: forceUpdateKioskSession })
}

export function useForceUpdateAllKioskSessionsMutation() {
  return useMutation({ mutationFn: forceUpdateAllKioskSessions })
}

export function useClearKioskSoftWarnMutation(locationId: string | null) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: clearKioskSoftWarn,
    onSuccess: () => {
      if (locationId) {
        void qc.invalidateQueries({ queryKey: kioskSessionsForLocationKey(locationId) })
      }
    },
  })
}

// ---------------------------------------------------------------------------
// Heartbeat badge — pure helper exported for testing
// ---------------------------------------------------------------------------

export type HeartbeatStatus = 'never' | 'online' | 'recent' | 'stale'

/**
 * Compute the heartbeat badge state from `lastHeartbeatAt`.
 *
 *   < 2 min  → 'online'  (green)
 *   < 10 min → 'recent'  (yellow)
 *   ≥ 10 min → 'stale'   (red)
 *   null     → 'never'   (gray)
 *
 * Pure — exposed for unit testing.
 */
export function heartbeatStatusForElapsed(elapsedMs: number | null): HeartbeatStatus {
  if (elapsedMs === null) {
    return 'never'
  }
  if (elapsedMs < 2 * 60 * 1000) {
    return 'online'
  }
  if (elapsedMs < 10 * 60 * 1000) {
    return 'recent'
  }
  return 'stale'
}

export function heartbeatStatusForRow(row: KioskSessionWire, now: number = Date.now()): HeartbeatStatus {
  if (!row.lastHeartbeatAt) {
    return 'never'
  }
  const elapsed = now - new Date(row.lastHeartbeatAt).getTime()
  return heartbeatStatusForElapsed(elapsed)
}

/**
 * Generate a stable browser identifier for kiosk pairing. The kiosk
 * persists this UUID across page reloads (this is what the manager's
 * drift rule compares against). For v1 we store it in localStorage —
 * a tablet that clears localStorage loses pairing, which is acceptable
 * (the same UX as revocation).
 *
 * Exposed for tests; the pair-sheet calls this before firing the
 * pair mutation.
 */
export function getOrCreateBrowserIdentifier(): string {
  if (typeof window === 'undefined' || typeof localStorage === 'undefined') {
    // SSR / tests fall back to a stable string; the real pairing flow
    // never runs server-side.
    return 'ssr-fallback'
  }
  const KEY = 'ordoBrowserId'
  const existing = localStorage.getItem(KEY)
  if (existing && existing.length > 0) {
    return existing
  }
  const fresh =
    typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function'
      ? crypto.randomUUID()
      : `browser_${Math.random().toString(36).slice(2)}${Date.now().toString(36)}`
  try {
    localStorage.setItem(KEY, fresh)
  } catch {
    // Quota or disabled — return the fresh value anyway; the caller
    // will just generate a new one next time (acceptable degradation).
  }
  return fresh
}

/**
 * Best-effort coarse hardware hint for the kiosk pair. The drift
 * rule treats this as a soft signal; on most tablets it's just
 * "ipad" or "android-tablet" derived from userAgent.
 *
 * Exposed for tests.
 */
export function generateHardwareHint(userAgent: string | null = null): string | null {
  const ua = userAgent ?? (typeof navigator !== 'undefined' ? navigator.userAgent : '')
  if (!ua) {
    return null
  }
  if (/ipad/i.test(ua)) {
    return 'ipad'
  }
  if (/iphone/i.test(ua)) {
    return 'iphone'
  }
  if (/android/i.test(ua)) {
    return /tablet/i.test(ua) ? 'android-tablet' : 'android-phone'
  }
  if (/macintosh/i.test(ua)) {
    return 'mac'
  }
  if (/windows/i.test(ua)) {
    return 'windows'
  }
  if (/cros/i.test(ua)) {
    return 'chromeos'
  }
  return null
}
